import { baseCrudFactory } from "~/stores/base.crud.store"
import { DateModel } from "~/model"
import { useProjetStore, usePersonnelStore, useRdvStore } from "~/stores"
import { groupById } from "~/helpers"
import { DevisModel } from "~/model"

const INDICE_CONFIANCE_PERC: Record<number, number> = {
    1: 5,
    2: 25,
    3: 75,
    4: 95,
}

export const useDevisStore = baseCrudFactory({
    endpoint: "devis",
    primaryKey: "IDDEVIS",
    title: "devis",
    startDateKey: "DATE_CREATION",
    endDateKey: "DATE_CREATION",
    model: DevisModel,
    state: {
        agencySelected: { NOM_AGENCE: "Toutes les agences", IDAGENCES: [] },
        waitingItemIds: [] as number[],
        birthdayItemIds: [] as number[],
    },
    modules: {
        projet: () => useProjetStore(),
        rdvs: () => useRdvStore(),
    },
    actions: {
        async fetchWaitingForProjection({ DATE_DEBUT, DATE_FIN, ALL }) {
            const response = await this.api.quote.devisIndex(
                DATE_DEBUT,
                DATE_FIN,
                "attente",
                ALL,
            )
            this.setItems(response ?? [], false)
            this.waitingItemIds = (response ?? []).map((d) => Number(d.IDDEVIS))
            return response
        },
        async fetchBirthday({ DATE_DEBUT, DATE_FIN, ALL }) {
            const response = await this.api.quote.devisIndex(
                DATE_DEBUT,
                DATE_FIN,
                "birthday" as any,
                ALL,
            )
            this.setItems(response ?? [], false)
            this.birthdayItemIds = (response ?? []).map((d) =>
                Number(d.IDDEVIS),
            )
            return response
        },
    },
    getters: {
        waitingItems(): DevisModel[] {
            const ids = new Set(this.waitingItemIds.map(Number))
            return this.items.filter((item) => ids.has(Number(item.IDDEVIS)))
        },
        birthdayItems(): DevisModel[] {
            const ids = new Set(this.birthdayItemIds.map(Number))
            return this.items.filter((item) => ids.has(Number(item.IDDEVIS)))
        },
        rankingItem(): DevisModel[] {
            return this.items.filter((quote) => {
                return (
                    quote.DATE_SIGNATURE &&
                    (DateModel.isInterval(
                        quote.DATE_SIGNATURE,
                        quote.DATE_SIGNATURE,
                        this.currentPeriod.startDate,
                        this.currentPeriod.endDate,
                    ) ||
                        DateModel.isInterval(
                            quote.DATE_REFUSE,
                            quote.DATE_REFUSE,
                            this.currentPeriod.startDate,
                            this.currentPeriod.endDate,
                        ))
                )
            })
        },
        personnelStore() {
            return usePersonnelStore()
        },
        devisByIdPersonnelFiltered() {
            const agencyIds = this?.agencySelected.IDAGENCES
            const allowedIds = this.personnelStore.commerciauxInStats.map((p) =>
                Number(p.IDPERSONNEL),
            )

            const filtered = this.rankingItem
                .filter((d) =>
                    allowedIds.includes(Number(d.IDRESPONSABLEPROJET)),
                )
                .filter(
                    (d) =>
                        agencyIds.length === 0 ||
                        agencyIds.includes(d.PROJET?.IDAGENCE),
                )

            return groupById(filtered, "IDRESPONSABLEPROJET")
        },
        totalNetHTByIdPersonnel() {
            return Object.entries(this.devisByIdPersonnelFiltered)
                .map(([IDPERSONNEL, devis]) => ({
                    IDPERSONNEL,
                    TOTAL: devis.reduce((acc, obj) => {
                        const signInMonth = !obj.DATE_SIGNATURE
                            ? false
                            : DateModel.isBetween(
                                  obj.DATE_SIGNATURE,
                                  this.currentPeriod.startDate,
                                  this.currentPeriod.endDate,
                              )
                        const refuseInMonth = !obj.DATE_REFUSE
                            ? false
                            : DateModel.isBetween(
                                  obj.DATE_REFUSE,
                                  this.currentPeriod.startDate,
                                  this.currentPeriod.endDate,
                              )

                        if (signInMonth && !refuseInMonth) {
                            return acc + obj.NET_HT
                        }
                        if (!signInMonth && refuseInMonth) {
                            return acc - obj.NET_HT
                        }

                        return acc
                    }, 0),
                }))
                .sort((a, b) => b.TOTAL - a.TOTAL)
        },
        totalCA() {
            return this.totalNetHTByIdPersonnel.reduce(
                (acc, { TOTAL }) => acc + TOTAL,
                0,
            )
        },
        projectionByIdPersonnel(): Record<string, number> {
            const agencyIds = this?.agencySelected.IDAGENCES
            const allowedIds = this.personnelStore.commerciauxInStats.map((p) =>
                Number(p.IDPERSONNEL),
            )

            const result: Record<string, number> = {}
            for (const devis of this.waitingItems) {
                const idResp = Number(devis.IDRESPONSABLEPROJET)
                if (!allowedIds.includes(idResp)) continue
                if (
                    agencyIds.length > 0 &&
                    !agencyIds.includes(devis.PROJET?.IDAGENCE)
                )
                    continue

                const perc = INDICE_CONFIANCE_PERC[devis.INDICE_CONFIANCE] ?? 0
                if (!perc) continue

                const amount = (Number(devis.NET_HT) * perc) / 100
                const key = String(devis.IDRESPONSABLEPROJET)
                result[key] = (result[key] ?? 0) + amount
            }
            return result
        },
        totalProjectionCA(): number {
            return Object.values(this.projectionByIdPersonnel).reduce(
                (acc, v) => acc + v,
                0,
            )
        },
        rankingByIdPersonnel(): {
            IDPERSONNEL: string
            TOTAL: number
            PROJECTION: number
        }[] {
            const map = new Map<
                string,
                { IDPERSONNEL: string; TOTAL: number; PROJECTION: number }
            >()

            for (const item of this.totalNetHTByIdPersonnel) {
                const key = String(item.IDPERSONNEL)
                map.set(key, {
                    IDPERSONNEL: key,
                    TOTAL: item.TOTAL,
                    PROJECTION: 0,
                })
            }

            for (const [id, projection] of Object.entries(
                this.projectionByIdPersonnel,
            )) {
                const existing = map.get(id)
                if (existing) {
                    existing.PROJECTION = projection
                } else {
                    map.set(id, {
                        IDPERSONNEL: id,
                        TOTAL: 0,
                        PROJECTION: projection,
                    })
                }
            }

            return Array.from(map.values()).sort(
                (a, b) => b.TOTAL + b.PROJECTION - (a.TOTAL + a.PROJECTION),
            )
        },
    },
    filters: {
        attente: (quote) => {
            return Number(quote.REFUSE) === 0 && !quote.DEVIS_SIGNE
        },
    },
})
